import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { RingMeterComponent } from '../charts/ring-meter.component';
import { ReportChromeService } from '../report-chrome.service';
import { reportDuration } from '../report-duration';
import { ReportCountUpDirective } from '../report-countup.directive';
import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { ReportLevel, SubjectReportView } from '../report.model';

/** Variante de pastille par palier — même appariement que le reste du rapport. */
const BADGE_VARIANT: Record<ReportLevel, string> = {
  weak: 'danger',
  average: 'warning',
  good: 'info',
  excellent: 'success',
};

/**
 * Section « résumé du résultat » d'une matière : cinq cartes.
 *
 * Elles disent cinq choses différentes et ne se recoupent pas : la note du
 * test, le palier qu'elle donne, le compte de compétences acquises, la durée,
 * le verdict de maîtrise. Le **score** et l'**acquisition** cohabitent ici sans
 * jamais se mélanger — deux mesures, deux cartes, deux dénominateurs.
 *
 * L'hôte est en `display: contents` : la feuille de styles cible
 * `.report-main > .section`, et un élément de composant intercalé deviendrait
 * l'enfant flex de la grille à la place de la section.
 */
@Component({
  selector: 's2e-report-summary',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReportFmtPipe,
    ReportTPipe,
    ReportCountUpDirective,
    ReportRevealDirective,
    RingMeterComponent,
  ],
  templateUrl: './subject-summary.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectSummarySection {
  private readonly i18n = inject(ReportI18nService);
  protected readonly chrome = inject(ReportChromeService);

  readonly subject = input.required<SubjectReportView>();

  /**
   * Mise en forme du compteur animé.
   *
   * Une **note** : entière quand elle l'est, une décimale sinon. La fonction
   * est recréée à chaque bascule de langue — `t()` et `mark()` lisent le signal
   * de langue — ce qui relance le décompte ; sans cela le nombre resterait
   * écrit dans la locale précédente (voir {@link ReportCountUpDirective}).
   */
  protected readonly markFormat = computed<(value: number) => string>(() => {
    // Lecture nue du signal de langue : elle n'existe que pour la dépendance.
    this.i18n.lang();
    return (value: number) => this.i18n.mark(value);
  });

  /**
   * Les points bruts, sous le cadran qui porte la note sur 20.
   *
   * <p><c>null</c> quand l'épreuve ne porte aucun point : il n'y a alors pas de
   * barème à rappeler.</p>
   */
  protected readonly pointsVars = computed<Record<string, unknown> | null>(() => {
    const score = this.subject().score;
    if (score.maxScore <= 0) return null;
    return { score: { mark: score.score }, max: { n: score.maxScore } };
  });

  /** Durée de la séance, dans l'unité qui lui convient. Voir {@link reportDuration}. */
  protected readonly duration = computed(() =>
    reportDuration(this.subject().durationMinutes),
  );

  /**
   * Variante de la pastille du palier.
   *
   * Suit le palier atteint, là où la maquette figeait l'ambre du « moyen » de
   * son jeu d'essai : un plan de développement affiché en ambre se lirait comme
   * un avertissement.
   */
  protected readonly bandBadgeClass = computed(() => {
    const level = this.subject().score.level;
    return `badge badge--${level ? BADGE_VARIANT[level] : 'neutral'} badge--sm`;
  });
}
